import { Plus, Edit2, Trash2, X, KeyRound, CheckCircle2, Webhook, Link2Off } from 'lucide-react';
import { colors } from '../../styles/colors';
import { useConsultantsManagement } from '../../hooks/useConsultantsManagement';
import DeleteConfirmModal from '../modals/DeleteConfirmModal';

// --- Styles partagés dans ce fichier ---

const thStyle = { padding: '0.875rem 1rem', fontSize: '0.875rem', fontWeight: '600', color: colors.text.white, textAlign: 'left' };
const tdStyle = { padding: '0.875rem 1rem', color: colors.primary.gray };

const inputStyle = {
  padding: '0.75rem',
  border: `1px solid ${colors.secondary.lightGray}`,
  borderRadius: '0.5rem',
  fontSize: '1rem',
  fontFamily: '"Noto Sans", sans-serif',
  width: '100%',
  boxSizing: 'border-box',
};

const labelStyle = {
  display: 'block', marginBottom: '0.4rem',
  fontSize: '0.875rem', fontWeight: '500',
  color: colors.primary.gray, fontFamily: '"Noto Sans", sans-serif',
};

const actionBtn = (bg) => ({
  padding: '0.4rem', backgroundColor: bg, color: colors.text.white,
  border: 'none', borderRadius: '0.375rem', cursor: 'pointer',
  display: 'flex', alignItems: 'center', transition: 'opacity 150ms',
});

function StatutBadge({ statut }) {
  const actif = statut === 'Actif';
  return (
    <span style={{
      padding: '0.2rem 0.65rem', borderRadius: '1rem',
      fontSize: '0.8125rem', fontWeight: '500',
      backgroundColor: actif ? colors.background.paleGreen : colors.background.lightRed,
      color: actif ? colors.text.darkGreen2 : colors.text.darkRed,
    }}>
      {statut || 'Actif'}
    </span>
  );
}

function QontoToggle({ consultant, toggling, onToggle }) {
  const enabled = consultant.qontoEinvoicingEnabled;
  const disabledReason = !consultant.qontoCredentialsSet && !enabled;
  return (
    <button
      onClick={() => onToggle(consultant)}
      disabled={toggling}
      title={disabledReason ? 'Enregistrez d\'abord les identifiants Qonto' : (enabled ? 'Désactiver la facturation électronique' : 'Activer la facturation électronique')}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.35rem',
        padding: '0.25rem 0.65rem', borderRadius: '1rem', border: 'none',
        fontSize: '0.8125rem', fontWeight: '500',
        cursor: toggling ? 'wait' : 'pointer',
        opacity: disabledReason ? 0.5 : 1,
        backgroundColor: enabled ? colors.background.paleGreen : colors.secondary.lightGray,
        color: enabled ? colors.text.darkGreen2 : colors.secondary.mediumGray,
      }}
    >
      {enabled && <CheckCircle2 size={14} />}
      {enabled ? 'Qonto actif' : 'Format actuel'}
    </button>
  );
}

/**
 * Badge d'état pour une étape donnée (connexion OAuth, ou abonnement webhook) — deux badges
 * distincts plutôt qu'un seul indicateur combiné, pour que chaque étape soit explicite (ex:
 * connecté à Qonto mais webhook pas encore enregistré, après "Connecter" mais avant
 * "Enregistrer les webhooks").
 */
function StatusPill({ label, active, title }) {
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
        padding: '0.2rem 0.6rem', borderRadius: '1rem',
        fontSize: '0.75rem', fontWeight: '500',
        backgroundColor: active ? colors.background.paleGreen : colors.secondary.lightGray,
        color: active ? colors.text.darkGreen2 : colors.secondary.mediumGray,
      }}
    >
      {active && <CheckCircle2 size={12} />}
      {label}
    </span>
  );
}

/**
 * Bouton "déconnecter" compact (icône seule, lignes du tableau) — volontairement distinct du
 * bouton "supprimer" (rouge plein, Trash2) utilisé pour la suppression définitive d'un
 * consultant : icône différente (Link2Off). Gris et désactivé tant que rien n'est connecté
 * (ni connexion OAuth ni webhook), rouge et cliquable dès que l'un des deux est actif — même
 * principe que QontoDisconnectFullButton, en version compacte.
 */
function DisconnectButton({ onClick, active, title }) {
  const color = active ? colors.status.error : colors.secondary.mediumGray;
  return (
    <button
      onClick={onClick}
      disabled={!active}
      title={active ? title : 'Rien à déconnecter'}
      style={{
        padding: '0.4rem', backgroundColor: 'transparent', color,
        border: `1px solid ${color}`, borderRadius: '0.375rem',
        cursor: active ? 'pointer' : 'not-allowed',
        display: 'flex', alignItems: 'center', transition: 'opacity 150ms',
      }}
      onMouseEnter={(e) => { if (active) e.currentTarget.style.opacity = '0.7'; }}
      onMouseLeave={(e) => { e.currentTarget.style.opacity = '1'; }}
    ><Link2Off size={14} /></button>
  );
}

/**
 * Bouton "connecter" (webhooks Qonto), version pleine taille (barre d'outils, GIE) et compacte
 * (icône seule, lignes du tableau) — teal et cliquable tant que non connecté, grisé et
 * désactivé une fois la connexion OAuth active (rien à refaire tant qu'on ne s'est pas
 * déconnecté d'abord).
 */
function QontoConnectFullButton({ onClick, connected, label }) {
  const color = connected ? colors.secondary.mediumGray : colors.primary.teal;
  return (
    <button
      onClick={onClick}
      disabled={connected}
      title={connected ? 'Déjà connecté' : label}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.4rem',
        padding: '0.75rem 1.25rem',
        backgroundColor: colors.text.white, color,
        border: `1px solid ${color}`, borderRadius: '0.5rem',
        fontSize: '0.9375rem', fontWeight: '500',
        cursor: connected ? 'not-allowed' : 'pointer',
        fontFamily: '"Noto Sans", sans-serif',
      }}
    >
      <Webhook size={18} /> {label}
    </button>
  );
}

/**
 * Bouton "déconnecter" pleine taille, même gabarit que "Connecter le GIE" (icône à gauche,
 * texte, mêmes dimensions) — utilisé dans la barre d'outils. Gris tant que rien n'est connecté
 * (connexion OAuth et webhook tous les deux absents), rouge dès que l'un des deux est actif ;
 * toujours affiché (contrairement à DisconnectButton, conditionné à l'existence d'une
 * connexion) pour rester alignée à droite de "Enregistrer les webhooks".
 */
function QontoDisconnectFullButton({ onClick, active, label }) {
  const color = active ? colors.status.error : colors.secondary.mediumGray;
  return (
    <button
      onClick={onClick}
      disabled={!active}
      title={active ? label : 'Rien à déconnecter'}
      style={{
        display: 'flex', alignItems: 'center', gap: '0.4rem',
        padding: '0.75rem 1.25rem',
        backgroundColor: colors.text.white, color,
        border: `1px solid ${color}`, borderRadius: '0.5rem',
        fontSize: '0.9375rem', fontWeight: '500',
        cursor: active ? 'pointer' : 'not-allowed',
        fontFamily: '"Noto Sans", sans-serif',
      }}
    >
      <Link2Off size={18} /> {label}
    </button>
  );
}

function ConsultantsTab() {
  const {
    filteredConsultants, loading, error,
    searchTerm, setSearchTerm,
    showModal, modalMode, formData, setFormData, submitting,
    handleOpenModal, handleCloseModal, handleSubmit,
    showDeleteModal, itemToDelete, deleting,
    handleDeleteClick, handleDeleteConfirm, handleDeleteCancel,
    showQontoModal, qontoTarget, qontoLogin, setQontoLogin, qontoSecretKey, setQontoSecretKey, qontoSubmitting,
    handleOpenQontoModal, handleCloseQontoModal, handleSubmitQontoCredentials,
    togglingId, handleToggleEinvoicing,
    handleConnectQontoOAuth,
    gieQontoStatus, registeringWebhooks, handleRegisterWebhooks,
    disconnectTarget, disconnecting,
    handleOpenDisconnectQonto, handleCloseDisconnectQonto, handleConfirmDisconnectQonto,
  } = useConsultantsManagement();

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <div style={{ width: '40px', height: '40px', border: `4px solid ${colors.secondary.lightTeal}`, borderTop: `4px solid ${colors.primary.teal}`, borderRadius: '50%', animation: 'spin 1s linear infinite' }} />
      </div>
    );
  }

  return (
    <div>
      {/* Barre d'outils */}
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1.25rem', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Rechercher..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ ...inputStyle, width: '280px' }}
          />
          <button
            onClick={() => handleOpenModal('create')}
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.75rem 1.25rem',
              backgroundColor: colors.primary.teal, color: colors.text.white,
              border: 'none', borderRadius: '0.5rem',
              fontSize: '0.9375rem', fontWeight: '500', cursor: 'pointer',
              fontFamily: '"Noto Sans", sans-serif',
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#005f6a'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = colors.primary.teal}
          >
            <Plus size={18} /> Ajouter
          </button>
        </div>

        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
          <span style={{ fontSize: '0.8125rem', fontWeight: '500', color: colors.secondary.mediumGray, marginRight: '0.2rem' }}>
            GIE — webhooks Qonto :
          </span>
          <StatusPill label="Connexion" active={gieQontoStatus.oauthConnected} title="Consentement OAuth Qonto" />
          <StatusPill label="Webhook" active={gieQontoStatus.webhookConnected} title="Abonnement webhook créé côté Qonto" />
          <QontoConnectFullButton
            onClick={() => handleConnectQontoOAuth('gie')}
            connected={gieQontoStatus.oauthConnected}
            label="Connecter le GIE"
          />
          <button
            onClick={handleRegisterWebhooks}
            disabled={registeringWebhooks}
            title="Créer/recréer les abonnements webhook Qonto pour toutes les organisations connectées (GIE + consultants)"
            style={{
              display: 'flex', alignItems: 'center', gap: '0.4rem',
              padding: '0.75rem 1.25rem',
              backgroundColor: colors.primary.teal, color: colors.text.white,
              border: 'none', borderRadius: '0.5rem',
              fontSize: '0.9375rem', fontWeight: '500',
              cursor: registeringWebhooks ? 'wait' : 'pointer',
              opacity: registeringWebhooks ? 0.7 : 1,
              fontFamily: '"Noto Sans", sans-serif',
            }}
            onMouseEnter={(e) => { if (!registeringWebhooks) e.currentTarget.style.backgroundColor = '#005f6a'; }}
            onMouseLeave={(e) => { if (!registeringWebhooks) e.currentTarget.style.backgroundColor = colors.primary.teal; }}
          >
            {registeringWebhooks ? 'Enregistrement...' : 'Enregistrer les webhooks'}
          </button>
          <QontoDisconnectFullButton
            onClick={() => handleOpenDisconnectQonto('gie', 'Le GIE')}
            active={gieQontoStatus.oauthConnected || gieQontoStatus.webhookConnected}
            label="Déconnecter le GIE"
          />
        </div>
      </div>

      {error && (
        <div style={{ backgroundColor: colors.background.lightRed, border: `1px solid ${colors.status.error}`, color: colors.text.darkRed, padding: '0.75rem 1rem', borderRadius: '0.5rem', marginBottom: '1rem', fontFamily: '"Noto Sans", sans-serif' }}>
          {error}
        </div>
      )}

      {/* Tableau */}
      <div style={{ backgroundColor: colors.text.white, borderRadius: '0.75rem', boxShadow: '0 2px 8px rgba(0,0,0,0.08)', overflow: 'hidden' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontFamily: '"Noto Sans", sans-serif' }}>
          <thead>
            <tr style={{ backgroundColor: colors.primary.gray }}>
              <th style={thStyle}>Nom</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Statut</th>
              <th style={thStyle}>Société propre</th>
              <th style={thStyle}>SIRET</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Facturation électronique</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Webhooks Qonto</th>
              <th style={{ ...thStyle, textAlign: 'center' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredConsultants.map((c, i) => (
              <tr key={c.id} style={{ backgroundColor: i % 2 === 0 ? colors.text.white : colors.secondary.lightGray, borderBottom: `1px solid ${colors.secondary.lightGray}` }}>
                <td style={{ ...tdStyle, fontWeight: '500' }}>{c.nom}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}><StatutBadge statut={c.statut} /></td>
                <td style={{ ...tdStyle, color: colors.secondary.mediumGray }}>{c.societe_propre || '-'}</td>
                <td style={{ ...tdStyle, color: colors.secondary.mediumGray, fontFamily: 'monospace' }}>{c.siret || '-'}</td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <QontoToggle consultant={c} toggling={togglingId === c.id} onToggle={handleToggleEinvoicing} />
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  {c.qontoCredentialsSet ? (
                    <div style={{ display: 'flex', gap: '0.35rem', justifyContent: 'center', alignItems: 'center', flexWrap: 'wrap' }}>
                      <StatusPill label="Connexion" active={c.qontoOAuthConnected} title="Consentement OAuth Qonto" />
                      <StatusPill label="Webhook" active={c.qontoWebhookConnected} title="Abonnement webhook créé côté Qonto" />
                      <button
                        onClick={() => handleConnectQontoOAuth(c.id)}
                        disabled={c.qontoOAuthConnected}
                        style={{
                          ...actionBtn(c.qontoOAuthConnected ? colors.secondary.mediumGray : colors.primary.teal),
                          cursor: c.qontoOAuthConnected ? 'not-allowed' : 'pointer',
                        }}
                        onMouseEnter={(e) => { if (!c.qontoOAuthConnected) e.currentTarget.style.opacity = '0.8'; }}
                        onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                        title={c.qontoOAuthConnected ? 'Déjà connecté' : 'Connecter aux webhooks Qonto (mise à jour automatique du statut Payée)'}
                      ><Webhook size={15} /></button>
                      <DisconnectButton
                        onClick={() => handleOpenDisconnectQonto(c.id, c.nom)}
                        active={c.qontoOAuthConnected || c.qontoWebhookConnected}
                        title="Déconnecter des webhooks Qonto"
                      />
                    </div>
                  ) : (
                    <span style={{ color: colors.secondary.mediumGray, fontSize: '0.8125rem' }}>—</span>
                  )}
                </td>
                <td style={{ ...tdStyle, textAlign: 'center' }}>
                  <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center' }}>
                    <button
                      onClick={() => handleOpenQontoModal(c)}
                      style={actionBtn(c.qontoCredentialsSet ? colors.primary.teal : colors.secondary.mediumGray)}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      title={c.qontoCredentialsSet ? 'Modifier les identifiants Qonto' : 'Renseigner les identifiants Qonto'}
                    ><KeyRound size={15} /></button>
                    <button
                      onClick={() => handleOpenModal('edit', c)}
                      style={actionBtn(colors.secondary.lightBlue)}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      title="Modifier"
                    ><Edit2 size={15} /></button>
                    <button
                      onClick={() => handleDeleteClick(c)}
                      style={actionBtn(colors.status.error)}
                      onMouseEnter={(e) => e.currentTarget.style.opacity = '0.8'}
                      onMouseLeave={(e) => e.currentTarget.style.opacity = '1'}
                      title="Supprimer"
                    ><Trash2 size={15} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredConsultants.length === 0 && (
          <div style={{ padding: '2.5rem', textAlign: 'center', color: colors.secondary.mediumGray, fontFamily: '"Noto Sans", sans-serif' }}>
            Aucun consultant trouvé
          </div>
        )}
      </div>

      {/* Modal création / édition */}
      {showModal && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: colors.text.white, borderRadius: '0.75rem', padding: '2rem', width: '90%', maxWidth: '480px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h2 style={{ fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif', fontSize: '1.25rem', fontWeight: 'bold', color: colors.primary.gray, margin: 0 }}>
                {modalMode === 'create' ? 'Ajouter un consultant' : 'Modifier le consultant'}
              </h2>
              <button onClick={handleCloseModal} style={{ padding: '0.4rem', background: 'none', border: 'none', cursor: 'pointer', color: colors.secondary.mediumGray }}>
                <X size={22} />
              </button>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={labelStyle}>Nom *</label>
                  <input type="text" required value={formData.nom} onChange={(e) => setFormData({ ...formData, nom: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Statut</label>
                  <select value={formData.statut} onChange={(e) => setFormData({ ...formData, statut: e.target.value })} style={{ ...inputStyle, cursor: 'pointer', backgroundColor: colors.text.white }}>
                    <option value="Actif">Actif</option>
                    <option value="Inactif">Inactif</option>
                  </select>
                </div>
                <div>
                  <label style={labelStyle}>Société propre</label>
                  <input type="text" value={formData.societe_propre} onChange={(e) => setFormData({ ...formData, societe_propre: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Format facture</label>
                  <input type="text" value={formData.format_facture} onChange={(e) => setFormData({ ...formData, format_facture: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>SIRET</label>
                  <input type="text" value={formData.siret} onChange={(e) => setFormData({ ...formData, siret: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Adresse</label>
                  <input type="text" value={formData.adresse} onChange={(e) => setFormData({ ...formData, adresse: e.target.value })} style={inputStyle} />
                </div>
                <div style={{ display: 'flex', gap: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <label style={labelStyle}>Code postal</label>
                    <input type="text" value={formData.code_postal} onChange={(e) => setFormData({ ...formData, code_postal: e.target.value })} style={inputStyle} />
                  </div>
                  <div style={{ flex: 2 }}>
                    <label style={labelStyle}>Ville</label>
                    <input type="text" value={formData.ville} onChange={(e) => setFormData({ ...formData, ville: e.target.value })} style={inputStyle} />
                  </div>
                </div>
                <div>
                  <label style={labelStyle}>Dossier Google Drive (ID)</label>
                  <input type="text" value={formData.gd_folder_id} onChange={(e) => setFormData({ ...formData, gd_folder_id: e.target.value })} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Dossier Dropbox</label>
                  <input type="text" value={formData.dropbox_folder} onChange={(e) => setFormData({ ...formData, dropbox_folder: e.target.value })} style={inputStyle} />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                  <button type="button" onClick={handleCloseModal} style={{ flex: 1, padding: '0.75rem', backgroundColor: colors.secondary.lightGray, color: colors.primary.gray, border: 'none', borderRadius: '0.5rem', fontSize: '1rem', cursor: 'pointer', fontFamily: '"Noto Sans", sans-serif' }}>
                    Annuler
                  </button>
                  <button type="submit" disabled={submitting} style={{ flex: 1, padding: '0.75rem', backgroundColor: submitting ? colors.secondary.mediumGray : colors.primary.teal, color: colors.text.white, border: 'none', borderRadius: '0.5rem', fontSize: '1rem', fontWeight: '500', cursor: submitting ? 'not-allowed' : 'pointer', fontFamily: '"Noto Sans", sans-serif' }}>
                    {submitting ? 'Enregistrement...' : (modalMode === 'create' ? 'Créer' : 'Modifier')}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal identifiants Qonto */}
      {showQontoModal && qontoTarget && (
        <div style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
          <div style={{ backgroundColor: colors.text.white, borderRadius: '0.75rem', padding: '2rem', width: '90%', maxWidth: '440px', boxShadow: '0 20px 40px rgba(0,0,0,0.15)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h2 style={{ fontFamily: '"Century Gothic Bold", "Noto Sans", sans-serif', fontSize: '1.25rem', fontWeight: 'bold', color: colors.primary.gray, margin: 0 }}>
                Identifiants Qonto — {qontoTarget.nom}
              </h2>
              <button onClick={handleCloseQontoModal} style={{ padding: '0.4rem', background: 'none', border: 'none', cursor: 'pointer', color: colors.secondary.mediumGray }}>
                <X size={22} />
              </button>
            </div>
            <p style={{ fontSize: '0.875rem', color: colors.secondary.mediumGray, fontFamily: '"Noto Sans", sans-serif', marginTop: 0, marginBottom: '1.25rem' }}>
              Identifiants de la clé API Qonto (Business API) du compte propre de ce consultant.
              Ils seront testés avant enregistrement, puis chiffrés.
            </p>
            <form onSubmit={handleSubmitQontoCredentials}>
              <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <div>
                  <label style={labelStyle}>Login</label>
                  <input type="text" required autoComplete="off" value={qontoLogin} onChange={(e) => setQontoLogin(e.target.value)} style={inputStyle} />
                </div>
                <div>
                  <label style={labelStyle}>Secret key</label>
                  <input type="password" required autoComplete="new-password" value={qontoSecretKey} onChange={(e) => setQontoSecretKey(e.target.value)} style={inputStyle} />
                </div>
                <div style={{ display: 'flex', gap: '0.75rem', marginTop: '0.5rem' }}>
                  <button type="button" onClick={handleCloseQontoModal} style={{ flex: 1, padding: '0.75rem', backgroundColor: colors.secondary.lightGray, color: colors.primary.gray, border: 'none', borderRadius: '0.5rem', fontSize: '1rem', cursor: 'pointer', fontFamily: '"Noto Sans", sans-serif' }}>
                    Annuler
                  </button>
                  <button type="submit" disabled={qontoSubmitting} style={{ flex: 1, padding: '0.75rem', backgroundColor: qontoSubmitting ? colors.secondary.mediumGray : colors.primary.teal, color: colors.text.white, border: 'none', borderRadius: '0.5rem', fontSize: '1rem', fontWeight: '500', cursor: qontoSubmitting ? 'not-allowed' : 'pointer', fontFamily: '"Noto Sans", sans-serif' }}>
                    {qontoSubmitting ? 'Vérification...' : 'Tester et enregistrer'}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Modal suppression */}
      <DeleteConfirmModal isOpen={showDeleteModal} onClose={handleDeleteCancel} onConfirm={handleDeleteConfirm} loading={deleting}>
        {itemToDelete && (
          <p style={{ margin: 0 }}>
            Supprimer le consultant <strong>{itemToDelete.nom}</strong> ?<br />
            <span style={{ color: colors.secondary.mediumGray, fontSize: '0.875rem' }}>Cette action est irréversible.</span>
          </p>
        )}
      </DeleteConfirmModal>

      {/* Modal déconnexion Qonto (webhooks) */}
      <DeleteConfirmModal
        isOpen={!!disconnectTarget}
        onClose={handleCloseDisconnectQonto}
        onConfirm={handleConfirmDisconnectQonto}
        loading={disconnecting}
        title="Déconnecter de Qonto ?"
      >
        {disconnectTarget && (
          <p style={{ margin: 0 }}>
            Déconnecter <strong>{disconnectTarget.label}</strong> des webhooks Qonto ?<br />
            <span style={{ color: colors.secondary.mediumGray, fontSize: '0.875rem' }}>
              Supprime l'abonnement webhook côté Qonto et la connexion enregistrée. Les identifiants Qonto (émission de factures) ne sont pas affectés.
            </span>
          </p>
        )}
      </DeleteConfirmModal>
    </div>
  );
}

export default ConsultantsTab;
